import AddIcon from "@mui/icons-material/Add";
import { Alert, Box, Button } from "@mui/material";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, { LazyTableRow, TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Application,
  ListRequestOfAdminApplicationFilter,
  ListResponseOfApplication,
  ListResponseOfSettingCache,
  SettingCache,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ApplicationOverrideForm from "./ApplicationOverrideForm";
import SettingValuePreview from "./SettingValuePreview";

type OverrideRow = LazyTableRow & {
  applicationName: string;
  settingValue: SettingCache;
  inheritedOrganizationName: string;
  onEdit: () => void;
  onDelete?: () => void;
};

const SettingValueCell: React.FC<{ value: OverrideRow }> = ({ value }) => (
  <SettingValuePreview value={value.settingValue} />
);

const ActionsCell: React.FC<{ value: OverrideRow }> = ({ value }) => {
  const row = value as OverrideRow;
  return (
    <Box display="flex" justifyContent="flex-end">
      <BasicActionsThreeDotsMenu onEdit={row.onEdit} onDelete={row.onDelete} />
    </Box>
  );
};

type ApplicationOverridesTableProps = {
  setting: SettingCache;
};

const ApplicationOverridesTable = ({
  setting,
}: ApplicationOverridesTableProps) => {
  const { formatMessage: t } = useIntl();
  const { settingService, applicationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [formOpen, formOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [selectedOverride, setSelectedOverride] = useState<
    SettingCache | undefined
  >();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const overridesResult = useQuery<ListResponseOfSettingCache>(
    ["settingService.getApplicationOverrides", setting.settingId],
    () => settingService.getApplicationOverrides(setting.settingId!),
    { enabled: !!setting.settingId },
  );

  const applicationsResult = useQuery<ListResponseOfApplication>(
    ["applicationService.getAdminApplications"],
    () =>
      applicationService.getAdminApplications(
        ListRequestOfAdminApplicationFilter.fromJS({ sortField: "name" }),
      ),
  );

  const getApplicationName = (applicationId?: string) => {
    if (!applicationId) return "-";
    const app = applicationsResult.data?.data?.find(
      (a: Application) => a.id === applicationId,
    );
    return app?.name ?? applicationId;
  };

  const handleAdd = () => {
    setSelectedOverride(undefined);
    formOpenActions.setTrue();
  };

  const handleEdit = (override: SettingCache) => {
    setSelectedOverride(override);
    formOpenActions.setTrue();
  };

  const handleDeleteClick = (override: SettingCache) => {
    setSelectedOverride(override);
    deleteOpenActions.setTrue();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedOverride?.settingValueId) {
        await settingService.deleteSettingValue(selectedOverride.settingValueId);
        queryClient.invalidateQueries([
          "settingService.getApplicationOverrides",
          setting.settingId,
        ]);
      }
    } catch (err: any) {
      setDeleteError(translateError(err, t));
    } finally {
      deleteOpenActions.setFalse();
      setSelectedOverride(undefined);
    }
  };

  const handleSaved = () => {
    queryClient.invalidateQueries([
      "settingService.getApplicationOverrides",
      setting.settingId,
    ]);
  };

  if (overridesResult.isLoading) return <Loader />;

  if (overridesResult.isError) {
    return (
      <Alert severity="error">
        <T id="applicationOverridesLoadError" />
      </Alert>
    );
  }

  const overrides = overridesResult.data?.data ?? [];
  const existingApplicationIds = overrides
    .map((o) => o.applicationId)
    .filter((id): id is string => !!id);

  const rows: OverrideRow[] = overrides.map((override) => ({
    id: override.applicationId ?? override.settingValueId ?? "",
    applicationName: getApplicationName(override.applicationId),
    settingValue: override,
    inheritedOrganizationName: override.inheritedOrganizationName ?? "-",
    onEdit: () => handleEdit(override),
    onDelete: override.ancestorSettingValueId
      ? undefined
      : () => handleDeleteClick(override),
  }));

  const cells: TableCellType[] = [
    {
      accessor: "applicationName",
      type: "text",
      label: t({ id: "applicationLabel" }),
      noSort: true,
    },
    {
      accessor: "settingValue",
      type: "node",
      label: t({ id: "valueLabel" }),
      View: SettingValueCell,
      noSort: true,
    },
    {
      accessor: "inheritedOrganizationName",
      type: "text",
      label: t({ id: "inheritedFrom" }),
      noSort: true,
    },
    {
      accessor: "actions",
      align: "right",
      width: 0,
      type: "node",
      label: "",
      View: ActionsCell,
      noSort: true,
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
          <T id="addApplicationOverride" />
        </Button>
      </Box>

      {deleteError && (
        <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}

      <LazyTable
        cells={cells}
        rows={rows}
        rowCount={rows.length}
        loading={overridesResult.isFetching}
        dense={true}
        allowPagination={false}
        page={0}
        setPage={() => {}}
        order="asc"
        setOrder={() => {}}
        orderBy=""
        setOrderBy={() => {}}
        rowsPerPage={rows.length || 10}
        setRowsPerPage={() => {}}
        noResultsLabel={t({ id: "noApplicationOverridesConfigured" })}
      />

      {formOpen && (
        <ApplicationOverrideForm
          setting={setting}
          override={selectedOverride}
          existingApplicationIds={existingApplicationIds}
          open={formOpen}
          onClose={formOpenActions.setFalse}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDialog
        dialogTitleKey="deleteApplicationOverride"
        rawContent={t(
          { id: "deleteApplicationOverrideConfirmation" },
          { application: getApplicationName(selectedOverride?.applicationId) },
        )}
        open={deleteOpen}
        onCancel={deleteOpenActions.setFalse}
        handleConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ApplicationOverridesTable;
